import { useEffect, useRef, useState } from 'react';
import AsyncSelect from 'react-select/async';
import Switch from 'react-switch';
import cogoToast from 'cogo-toast';
import _ from 'lodash';
import { getScriptArgOptions, isEntityIdInput, parseEntityListInput, shouldResolvePastedEntityList } from '../../helpers/script_runner_helpers';
import { getSearchIndex } from '../../helpers/search_helpers';

const isProduction = process.env.REACT_APP_NODE_ENV === 'production';
const searchIndexes = {
  brand: getSearchIndex(isProduction ? 'brands_production' : 'brands_staging'),
  user: getSearchIndex(isProduction ? 'sms_users_production' : 'sms_users_staging')
};

const entityToSelectOption = (entity, type) => ({
  value: entity.id,
  label: `${entity.name || 'Unnamed entity'}${type === 'user' && entity.username ? ` (@${entity.username})` : ''}${
    type === 'brand' && entity.domain ? ` (${entity.domain})` : ''
  } (#${entity.id})`
});
const isSelectableEntity = (entity, type) => entity.id !== null && entity.id !== undefined && (type !== 'brand' || entity.isPartner === true);

const loadEntityOptions = type => inputValue =>
  searchIndexes[type]
    .search(inputValue || '')
    .then(({ hits }) => hits.filter(entity => isSelectableEntity(entity, type)).map(entity => entityToSelectOption(entity, type)));
const getEntityResults = (type, selectedIds) =>
  type === 'brand'
    ? searchIndexes[type]
        .search('', {
          // id is a numeric attribute rather than a facet on the brands index, so only the numeric form matches.
          filters: selectedIds.map(id => `id = ${id}`).join(' OR '),
          hitsPerPage: selectedIds.length
        })
        .then(({ hits }) => hits)
    : searchIndexes[type].getObjects(selectedIds.map(String)).then(({ results }) => results.filter(Boolean));

// Algolia ranks exact username hits first, but a short query can still surface neighbors, so match the hit ourselves.
const getUserByUsername = username =>
  searchIndexes.user
    .search(username, { hitsPerPage: 20 })
    .then(({ hits }) => hits.find(hit => hit.username?.toLowerCase() === username.toLowerCase()) || null);

const resolvePastedEntities = (type, tokens) => {
  const ids = tokens.filter(isEntityIdInput);
  const usernames = type === 'user' ? tokens.filter(token => !isEntityIdInput(token)) : [];

  return Promise.all([ids.length ? getEntityResults(type, ids) : [], Promise.all(usernames.map(getUserByUsername))]).then(
    ([entitiesById, entitiesByUsername]) => {
      const nonPartnerIds = new Set(
        type === 'brand'
          ? entitiesById
              .filter(entity => entity && entity.id !== null && entity.id !== undefined && !isSelectableEntity(entity, type))
              .map(entity => String(entity.id).toLowerCase())
          : []
      );
      const entities = _.uniqBy(
        [...entitiesById, ...entitiesByUsername].filter(entity => entity && isSelectableEntity(entity, type)),
        entity => String(entity.id)
      );
      const matchedTokens = new Set([
        ...entities.flatMap(entity => [String(entity.id).toLowerCase(), entity.username?.toLowerCase()]),
        ...nonPartnerIds
      ]);
      return {
        entities,
        invalidTokens: tokens.filter(token => !matchedTokens.has(token.toLowerCase())),
        nonPartnerTokens: tokens.filter(token => nonPartnerIds.has(token.toLowerCase()))
      };
    }
  );
};

const getInvalidTokensMessage = (type, invalidTokens) =>
  `Could not find ${type}${invalidTokens.length === 1 ? '' : 's'}: ${invalidTokens.join(', ')}`;
const getNonPartnerTokensMessage = nonPartnerTokens => `Non-partner brand${nonPartnerTokens.length === 1 ? '' : 's'}: ${nonPartnerTokens.join(', ')}`;

const isBooleanArg = arg => arg.type === 'boolean' || arg.takesValue === false;

const EntityArgumentInput = ({ arg, type, value, onArgChange, areInputsReadOnly }) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const selectedOptionsRef = useRef([]);
  const [inputValue, setInputValue] = useState('');
  const [isResolvingPastedList, setIsResolvingPastedList] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const selectedIds = Array.isArray(value) ? value : value === undefined || value === null || value === '' ? [] : [value];

    if (selectedIds.length === 0) {
      selectedOptionsRef.current = [];
      setSelectedOptions([]);
      return undefined;
    }

    const fallbackOptions = selectedIds.map(id => ({ value: id, label: `${type === 'brand' ? 'Brand' : 'User'} #${id}` }));
    getEntityResults(type, selectedIds)
      .then(results => {
        if (!isMounted) return;
        const optionsById = results.reduce((options, entity) => {
          if (entity?.id !== null && entity?.id !== undefined) options[entity.id] = entityToSelectOption(entity, type);
          return options;
        }, {});
        const nextOptions = selectedIds.map(
          (id, index) =>
            optionsById[id] ||
            optionsById[String(id)] ||
            selectedOptionsRef.current.find(option => String(option.value) === String(id)) ||
            fallbackOptions[index]
        );
        selectedOptionsRef.current = nextOptions;
        setSelectedOptions(nextOptions);
      })
      .catch(() => {
        if (isMounted) {
          const nextOptions = selectedIds.map(
            (id, index) => selectedOptionsRef.current.find(option => String(option.value) === String(id)) || fallbackOptions[index]
          );
          selectedOptionsRef.current = nextOptions;
          setSelectedOptions(nextOptions);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [type, value]);

  const handleChange = options => {
    const nextOptions = options || [];
    selectedOptionsRef.current = nextOptions;
    setSelectedOptions(nextOptions);
    onArgChange(
      arg.flag,
      nextOptions.map(option => option.value)
    );
  };

  const handlePaste = event => {
    const pastedText = event.clipboardData?.getData('text') || '';
    const tokens = parseEntityListInput(pastedText);
    if (!shouldResolvePastedEntityList(tokens, type)) return;
    event.preventDefault();

    setIsResolvingPastedList(true);
    resolvePastedEntities(type, tokens)
      .then(({ entities, invalidTokens, nonPartnerTokens }) => {
        if (type === 'user' && entities.length === 0) {
          setInputValue(pastedText);
          return;
        }

        const currentOptions = selectedOptionsRef.current;
        const newOptions = entities
          .filter(entity => !currentOptions.some(option => String(option.value) === String(entity.id)))
          .map(entity => entityToSelectOption(entity, type));
        const mergedOptions = [...currentOptions, ...newOptions];
        selectedOptionsRef.current = mergedOptions;
        setSelectedOptions(mergedOptions);
        if (newOptions.length)
          onArgChange(
            arg.flag,
            mergedOptions.map(option => option.value)
          );
        if (invalidTokens.length) cogoToast.error(getInvalidTokensMessage(type, invalidTokens), { hideAfter: 8 });
        if (nonPartnerTokens.length) cogoToast.error(getNonPartnerTokensMessage(nonPartnerTokens), { hideAfter: 8 });
      })
      .catch(() => cogoToast.error(`There was an issue looking up the pasted ${type}s, please try again.`))
      .finally(() => setIsResolvingPastedList(false));
  };

  return (
    <div className='script-runner-entity-select-paste-target' onPaste={handlePaste}>
      <AsyncSelect
        inputId={`arg-${arg.flag}`}
        className='script-runner-entity-select'
        classNamePrefix='script-runner-entity-select'
        cacheOptions
        defaultOptions={false}
        isMulti
        isDisabled={areInputsReadOnly}
        isLoading={isResolvingPastedList}
        loadOptions={loadEntityOptions(type)}
        onChange={handleChange}
        onInputChange={nextInputValue => {
          setInputValue(nextInputValue);
          return nextInputValue;
        }}
        placeholder={areInputsReadOnly ? '' : `Search ${type}s or paste ${type === 'user' ? 'ids or usernames' : 'ids'}`}
        inputValue={inputValue}
        value={selectedOptions}
      />
    </div>
  );
};

const ScriptRunnerConfigurationForm = ({
  selectedConfig,
  selectedRun,
  argsSchema,
  argValues,
  onArgChange,
  areInputsReadOnly,
  runSuccess,
  runError,
  isSubmitting,
  onSubmit,
  onClearSelectedRun,
  onCreateRunWithInputs,
  onCancel,
  canRunSelectedScript
}) => {
  const inputArgs = argsSchema.filter(arg => !isBooleanArg(arg));
  const booleanArgs = argsSchema.filter(isBooleanArg);

  return (
    <form className='script-runner-section script-runner-form-section' onSubmit={onSubmit}>
      <div className='script-runner-section-content'>
        <h2>{selectedRun ? 'Configuration Used' : 'Configure This Run'}</h2>

        {argsSchema.length === 0 && <p className='script-runner-no-args-text'>No arguments required for this script.</p>}

        {inputArgs.map(arg => {
          const isOption = arg.type === 'option';
          const isEntity = arg.type === 'brand' || arg.type === 'user';

          return (
            <div key={arg.flag} className='script-runner-field'>
              <label htmlFor={`arg-${arg.flag}`} className='script-runner-label'>
                {arg.name}
                {arg.required && <span className='script-runner-required-star'>*</span>}
              </label>

              {isOption ? (
                (() => {
                  const options = getScriptArgOptions(arg);
                  const currentValue = argValues[arg.flag] === undefined || argValues[arg.flag] === null ? '' : String(argValues[arg.flag]);
                  return (
                    <select
                      id={`arg-${arg.flag}`}
                      className='script-runner-select'
                      value={currentValue}
                      onChange={e => onArgChange(arg.flag, e.target.value)}
                      required={arg.required}
                      disabled={areInputsReadOnly}
                    >
                      <option value=''>{areInputsReadOnly ? '' : 'Select an option'}</option>
                      {options.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                      {currentValue && !options.includes(currentValue) && <option value={currentValue}>{currentValue}</option>}
                    </select>
                  );
                })()
              ) : isEntity ? (
                <EntityArgumentInput
                  arg={arg}
                  type={arg.type}
                  value={argValues[arg.flag]}
                  onArgChange={onArgChange}
                  areInputsReadOnly={areInputsReadOnly}
                />
              ) : (
                <input
                  id={`arg-${arg.flag}`}
                  type={arg.type === 'number' ? 'number' : 'text'}
                  className='script-runner-input'
                  value={argValues[arg.flag] ?? ''}
                  placeholder={areInputsReadOnly ? '' : arg.description}
                  onChange={e => onArgChange(arg.flag, e.target.value)}
                  required={arg.required}
                  disabled={areInputsReadOnly}
                />
              )}

              {(isOption || isEntity) && arg.description && <span className='script-runner-arg-help'>{arg.description}</span>}
            </div>
          );
        })}

        {booleanArgs.length > 0 && (
          <div className='script-runner-toggles'>
            {booleanArgs.map(arg => (
              <div key={arg.flag} className='script-runner-toggle-row'>
                <Switch
                  checked={Boolean(argValues[arg.flag])}
                  onChange={checked => onArgChange(arg.flag, checked)}
                  disabled={areInputsReadOnly}
                  onColor='#61be91'
                  offColor='#e0e0e0'
                  checkedIcon={false}
                  uncheckedIcon={false}
                  height={24}
                  width={54}
                  handleDiameter={20}
                />
                <div className='script-runner-toggle-copy'>
                  <span className='script-runner-toggle-name'>{arg.name}</span>
                  {arg.description && <span className='script-runner-toggle-description'>{arg.description}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {runSuccess && <div className='script-runner-alert script-runner-alert-success'>{runSuccess}</div>}
        {runError && <div className='script-runner-alert script-runner-alert-error'>{runError}</div>}

        <div className='script-runner-actions'>
          {selectedRun ? (
            <>
              <button type='button' className='script-runner-primary-btn' onClick={onClearSelectedRun}>
                Create New Run
              </button>
              <button
                type='button'
                className='script-runner-secondary-btn'
                onClick={onCreateRunWithInputs}
                disabled={isSubmitting || !canRunSelectedScript}
              >
                Create New Run With Inputs
              </button>
            </>
          ) : (
            <button type='submit' className='script-runner-primary-btn' disabled={!selectedConfig || isSubmitting || !canRunSelectedScript}>
              {isSubmitting ? 'Triggering...' : selectedConfig.mustDryRun ? 'Run Dryrun' : 'Run'}
            </button>
          )}
          <button type='button' className='script-runner-secondary-btn' onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

export default ScriptRunnerConfigurationForm;
